import { Decorators, EntityGrid, GridUtils, LookupEditor, LookupEditorOptions, QuickFilterBar, SelectEditor, ToolButton, Widget } from "@serenity-is/corelib";
import { confirmDialog, Dictionary, format, isEmptyOrNull, isTrimmedEmpty, localText, notifyError, notifySuccess, outerHtml, serviceCall, ServiceResponse, trimToEmpty, trimToNull } from "@serenity-is/corelib/q";
import { TranslationItem, TranslationListResponse, TranslationUpdateRequest, TranslationUpdateResponse } from "@serenity-is/extensions";
import { Column } from "@serenity-is/sleekgrid";
import copyToClipboard from "../Tools/CopyToClipboard";

@Decorators.registerClass('Serenity.Extensions.TranslationGrid')
export class TranslationGrid extends EntityGrid<TranslationItem, any> {
    protected getIdProperty() { return "Key"; }
    protected getLocalTextDbPrefix() { return "Site.Translation."; }
    protected getService() { return "Administration/Translation" }

    private hasChanges: boolean;
    private searchText: string;
    private sourceLanguage: LookupEditor;
    private targetLanguage: LookupEditor;
    private targetLanguageKey: string;

    constructor(container: JQuery) {
        super(container);

        this.element.on('keyup.' + this.uniqueName + ' change.' + this.uniqueName,
            'input.custom-text', e => {
            var value = trimToNull($(e.target).val());
            if (value === '') {
                value = null;
            }
            this.view.getItemById($(e.target).data('key')).CustomText = value;
            this.hasChanges = true;
        });
    }

    protected onClick(e: JQueryEventObject, row: number, cell: number): any {
        super.onClick(e, row, cell);

        if (e.isDefaultPrevented()) {
            return;
        }

        let item = this.itemAt(row);
        let done: () => void;

        if ($(e.target).hasClass('source-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.SourceText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (trimToEmpty(item.CustomText) === trimToEmpty(item.SourceText))) {
                done();
                return;
            }

            confirmDialog(localText('Site.Translation.OverrideConfirmation'), done);
            return;
        }

        if ($(e.target).hasClass('target-text')) {
            e.preventDefault();

            done = () => {
                item.CustomText = item.TargetText;
                this.view.updateItem(item.Key, item);
                this.hasChanges = true;
            };

            if (isTrimmedEmpty(item.CustomText) ||
                (trimToEmpty(item.CustomText) === trimToEmpty(item.TargetText))) {
                done();
                return;
            }

            confirmDialog(localText('Site.Translation.OverrideConfirmation'), done);
            return;
        }
    }

    protected getColumns(): Column[] {

        var columns: Column[] = [];
        columns.push({ field: 'Key', width: 300, sortable: false });

        columns.push({
            field: 'SourceText',
            width: 300,
            sortable: false,
            format: ctx => {
                return outerHtml($('<a/>')
                    .addClass('source-text')
                    .text(ctx.value || ''));
            }
        });

        columns.push({
            field: 'CustomText',
            width: 300,
            sortable: false,
            format: ctx => outerHtml($('<input/>')
                .addClass('custom-text')
                .attr('value', ctx.value)
                .attr('type', 'text')
                .attr('data-key', ctx.item.Key))
        });

        columns.push({
            field: 'TargetText',
            width: 300,
            sortable: false,
            format: ctx => outerHtml($('<a/>')
                .addClass('target-text')
                .text(ctx.value || ''))
        });

        return columns;
    }

    protected createToolbarExtensions(): void {
        super.createToolbarExtensions();

        let opt: LookupEditorOptions = {
            lookupKey: 'Administration.Language'
        };

        this.sourceLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.element).attr('placeholder', '--- ' +
                localText('Site.Translation.SourceLanguage') + ' ---'),
            options: opt
        });

        this.sourceLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });

        this.targetLanguage = Widget.create({
            type: LookupEditor,
            element: el => el.appendTo(this.toolbar.element).attr('placeholder', '--- ' +
                localText('Site.Translation.TargetLanguage') + ' ---'),
            options: opt
        });

        this.targetLanguage.changeSelect2(e => {
            if (this.hasChanges) {
                this.saveChanges(this.targetLanguageKey).then(() => this.refresh());
            }
            else {
                this.refresh();
            }
        });
    }

    protected getQuickFilters() {
        var filters = super.getQuickFilters();

        var userTranslated = QuickFilterBar.boolean('UserTranslated',
            localText('Site.Translation.UserTranslated'));
        userTranslated.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(userTranslated);

        var hasTranslation = QuickFilterBar.boolean('HasTranslation',
            localText('Site.Translation.HasTranslation'));
        hasTranslation.init = w => w.changeSelect2(() => this.hasChanges && this.saveChanges(this.targetLanguageKey));

        filters.push(hasTranslation);

        filters.push({
            field: 'Assembly',
            cssClass: 'Assembly',
            title: localText('Site.Translation.Assembly'),
            type: SelectEditor
        });

        return filters;
    }

    private keysByAssembly: Dictionary<Dictionary<boolean>>;

    protected onViewProcessData(response: TranslationListResponse) {
        response = super.onViewProcessData(response);
        this.keysByAssembly = {};
        var assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly) {
            var value = assembly.value;
            assembly.clearItems();
            var asmNames = Object.keys(response.KeysByAssembly);
            asmNames.sort();
            for (var asmName of asmNames) {
                var asmKeys = response.KeysByAssembly[asmName];
                if (!asmKeys?.length)
                    continue;
                assembly.addOption(asmName, asmName);
                var keys = this.keysByAssembly[asmName] = {};
                for (var key of asmKeys)
                    keys[key] = true;
            }
            assembly.element.select2('val', value);
        }
        return response;
    }

    protected saveChanges(language: string): PromiseLike<any> {
        var translations: { [key: string]: string } = {};
        for (let item of this.getItems()) {
            translations[item.Key] = item.CustomText;
        }

        return serviceCall<ServiceResponse>({
            service: this.getService() + "/Update",
            request: <TranslationUpdateRequest>{
                TargetLanguageID: language,
                Translations: translations
            }
        }).then((response: TranslationUpdateResponse) => {
            this.hasChanges = false;
            notifySuccess(format(localText('Site.Translation.SaveSuccessMessage'), response.SavedPath));
        });
    }

    protected onViewSubmit(): boolean {
        var request = this.view.params;
        request.SourceLanguageID = this.sourceLanguage.value;
        this.targetLanguageKey = this.targetLanguage.value || '';
        request.TargetLanguageID = this.targetLanguageKey;
        this.hasChanges = false;
        return super.onViewSubmit();
    }

    protected getButtons(): ToolButton[] {
        return [
            {
                title: localText('Site.Translation.SaveChangesButton'),
                onClick: _ => this.saveChanges(this.targetLanguageKey).then(() => this.refresh()),
                cssClass: 'apply-changes-button'
            },
            {
                hint: localText("Site.Translation.CopySourceTranslations"),
                cssClass: 'copy-source-to-clipboard',
                icon: 'fa fa-clone',
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++) {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.SourceText;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(localText("Site.Translation.CopySuccessMessage")))
                        .catch(() => notifyError(localText("Site.Translation.CopyFailMessage")))
                }
            },
            {
                hint: localText("Site.Translation.CopyTargetTranslations"),
                cssClass: 'copy-target-to-clipboard',
                icon: 'fa fa-copy',
                onClick: () => {
                    var length = this.slickGrid.getDataLength();
                    var d = {};
                    for (var i = 0; i < length; i++)
                    {
                        var item = this.slickGrid.getDataItem(i) as TranslationItem;
                        d[item.Key] = item.CustomText?.length ? item.CustomText :
                            item.HasTranslation ? item.TargetText : null;
                    }

                    copyToClipboard(JSON.stringify(d, null, '  '))
                        .then(() => notifySuccess(localText("Site.Translation.CopySuccessMessage")))
                        .catch(() => notifyError(localText("Site.Translation.CopyFailMessage")))
                }
            }
        ];
    }

    protected createQuickSearchInput() {
        GridUtils.addQuickSearchInputCustom(this.toolbar.element,
            (_, searchText, done) => {
                this.searchText = searchText;
                this.view.setItems(this.view.getItems(), true);
                done(this.slickGrid.getDataLength() > 0);
            });
    }

    protected onViewFilter(item: TranslationItem) {
        if (!super.onViewFilter(item))
            return false;

        var userTranslated = this.tryFindQuickFilter(SelectEditor, "UserTranslated");
        if (userTranslated?.value?.length) {
            if (userTranslated.value == "1" && !item.UserTranslated)
                return false;

            if (userTranslated.value == "0" && item.UserTranslated)
                return false;
        }

        var hasTranslation = this.tryFindQuickFilter(SelectEditor, "HasTranslation");
        if (hasTranslation?.value?.length) {
            if (hasTranslation.value == "1" && !item.HasTranslation)
                return false;

            if (hasTranslation.value == "0" && item.HasTranslation)
                return false;
        }

        var assembly = this.tryFindQuickFilter(SelectEditor, "Assembly");
        if (assembly?.value?.length) {
            var kba = (this.keysByAssembly || {})[assembly.value];
            if (!kba || !kba[item.Key])
                return false;
        }

        if (!this.searchText)
            return true;

        var sd = Select2.util.stripDiacritics;
        var searching = sd(this.searchText).toLowerCase();

        function match(str: string) {
            if (!str)
                return false;

            return str.toLowerCase().indexOf(searching) >= 0;
        }

        return isEmptyOrNull(searching) || match(item.Key) || match(item.SourceText) ||
            match(item.TargetText) || match(item.CustomText);
    }

    protected usePager() {
        return false;
    }
}